import { useEffect, useRef, useState } from "react";
import StepProgressBar from "../../forms/subcribirseForm/StepProgressBarraProfe";
import FormularioPaso1 from "../../forms/subcribirseForm/FormularioPaso1";
import FormularioPaso2 from "../../forms/subcribirseForm/FormularioPaso2";
import FormularioPaso3 from "../../forms/subcribirseForm/FormularioPaso3";
import { ButtonSiguiente } from "../../ui";
import {
  ContrasenaProps,
  Datos4Props,
  FormData1,
  FormData2,
  FormData3,
  PaymentOption,
  TokenPayload,
} from "@/types";
import {
  isValidPhone,
  ValidateDNI,
  ValidateCIF,
  ValidateNIE,
  isValidContrasena,
} from "../../validation/validations";
import { jwtDecode } from "jwt-decode";
import { FacturaPdfRef } from "../../forms/subcribirseForm/contratoServicios/FacturaPDF";
import { safeFetch } from "@/api/safeFetch";
/* import { useAuthToken } from "@/hooks/useAuthToken"; */
import { Role } from "@/utils/rbac";

// Tipo combinado de todos los formularios del usuario
type FormDataUsuario = FormData1 & ContrasenaProps & FormData3 & Datos4Props;
type FormErrors = Partial<Record<keyof FormDataUsuario | keyof FormData2, boolean>>;
// 1️⃣ Actualizamos el tipo de formDataPaso1
type FormDataPaso1Type = FormData1 & ContrasenaProps & { usuario_id?: number };

const Subs: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isEmpresa, setIsEmpresa] = useState(false);

  // ⚡ Inicializamos paymentOption con un valor válido
  const [paymentOption, setPaymentOption] = useState<PaymentOption>("Anual");
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [isFirmaAccepted] = useState(false);
  const [userName, setUserName] = useState<string | null>(null);
  const [roles, setRoles] = useState<Role[] | null>(null);


  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [, setTotalPagar] = useState<number>(0);

  const facturaRef = useRef<FacturaPdfRef>(null);

  // Estado principal del usuario (se usará para validaciones / envío)
  const [formData1, setFormData1] = useState<FormDataUsuario>({
    nombre: "",
    nombre_usuario: "",
    apellidos: "",
    email: "",
    numero_telefono: "",
    idSelectDniNie: "",
    dniNie: "",
    calle: "",
    locality: "",
    province: "",
    postalCode: "",
    nombre_empresa: "",
    cif: "",
    contrasena: "",
    confirmarContrasena: "",
    paymentOption: "Anual",
    paymentAmount: 0,
    selectedProfession: [],
    totalPago: 0,
  });

  // 2️⃣ Estado para el Paso 1
  const [formDataPaso1, setFormDataPaso1] = useState<FormDataPaso1Type>({
    nombre: "",
    nombre_usuario: "",
    apellidos: "",
    email: "",
    numero_telefono: "",
    idSelectDniNie: "",
    dniNie: "",
    calle: "",
    locality: "",
    province: "",
    postalCode: "",
    nombre_empresa: "",
    cif: "",
    contrasena: "",
    confirmarContrasena: "",
    usuario_id: undefined,
  });

  // 3️⃣ Sincronizar formDataPaso1 -> formData1 (principal)
  useEffect(() => {
    setFormData1(prev => ({ ...prev, ...formDataPaso1 }));
  }, [formDataPaso1]);

  const [formData2, setFormData2] = useState<FormData2>({
    selectedProfession: [],
    payment: 0,
    paymentOption: "Anual",
  });

  const [unlockDni] = useState(false);
  // Permite desbloquear el campo DNI/NIE para usuarios que se suscriben como profesionales


  // Sincronizar formDataPaso1 -> formData1 para que validaciones / envío siempre vean los últimos valores
  /*   useEffect(() => {
      setFormData1(prev => ({ ...prev, ...formDataPaso1 }));
    }, [formDataPaso1]);
   */
  const validateDniCif = () => {
    const { dniNie, idSelectDniNie, cif } = formData1;
    const tipo = idSelectDniNie?.toLowerCase();

    if (tipo === "dni" && !ValidateDNI(dniNie || "")) return false;
    if (tipo === "nie" && !ValidateNIE(dniNie || "")) return false;
    if (isEmpresa && (!cif || !ValidateCIF(cif))) return false;
    return true;
  };

  const validateStep = (): boolean => {
    const newErrors: FormErrors = {};

    if (currentStep === 1) {
      // ahora usamos formData1 (sincronizado con formDataPaso1)
      const requiredFields = [
        "nombre", "apellidos", "email", "numero_telefono",
        "idSelectDniNie", "dniNie", "calle", "locality", "province", "postalCode"
      ];
      requiredFields.forEach((field) => {
        if (!formData1[field as keyof FormDataUsuario]) newErrors[field as keyof FormDataUsuario] = true;
      });

      if (!/^\d{5}$/.test(formData1.postalCode || "")) newErrors.postalCode = true;
      if (formData1.numero_telefono && !isValidPhone(formData1.numero_telefono)) {
        newErrors.numero_telefono = true;
        setFormData1((prev) => ({ ...prev, numero_telefono: "" }));
      }

      if (isEmpresa) {
        if (!formData1.nombre_empresa) newErrors.nombre_empresa = true;
        if (!formData1.cif || !ValidateCIF(formData1.cif)) newErrors.cif = true;
      }

      if (!validateDniCif()) newErrors.dniNie = true;
      if (!userName) {
        const passwordError = isValidContrasena(formData1.contrasena || "");

        if (!formData1.contrasena || passwordError !== null) {
          newErrors.contrasena = true;
        }
        if (formData1.contrasena !== formData1.confirmarContrasena) {
          newErrors.confirmarContrasena = true;
        }
      }
    }

    if (currentStep === 2) {
      if (!formData2.selectedProfession?.length) newErrors.selectedProfession = true;
      if (!formData2.payment) newErrors.payment = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 4️⃣ Decodificación del token
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decodedToken: TokenPayload = jwtDecode<TokenPayload>(token);

        if (decodedToken.username) setUserName(decodedToken.username);

        // ✔ Guardar roles correctamente como array
        if (decodedToken.roles) {
          setRoles(decodedToken.roles); // guarda todos los roles
          // o si quieres solo el principal:
          // setRoles([decodedToken.roles[0]]);
        }

        if (decodedToken.usuario_id) {
          setFormData1(prev => ({ ...prev, usuario_id: decodedToken.usuario_id }));
          setFormDataPaso1(prev => ({ ...prev, usuario_id: decodedToken.usuario_id }));
        }

      } catch (err) {
        console.error("Error decoding token:", err);
      }
    }
  }, []);


  // Fetch datos del suscriptor: rellenamos tanto formData1 como formDataPaso1
  useEffect(() => {
    if (!userName || currentStep > 1) return;

    const fetchSuscriptor = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("Token no disponible");

        const response = await safeFetch(`/api/suscribirse/${userName}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Error al obtener datos del suscriptor");
        const data: FormDataUsuario[] = await response.json();
        if (data.length > 0) {
          const fetched = data[0];

          // 1) rellenar formData1 (estado principal)
          setFormData1(prev => ({ ...prev, ...fetched }));

          // 2) rellenar formDataPaso1 (inputs del paso 1) — solo campos que existen en ese tipo
          setFormDataPaso1(prev => ({
            ...prev,
            nombre: fetched.nombre ?? prev.nombre,
            nombre_usuario: (fetched.nombre_usuario as string) ?? prev.nombre_usuario,
            apellidos: fetched.apellidos ?? prev.apellidos,
            email: fetched.email ?? prev.email,
            numero_telefono: fetched.numero_telefono ?? prev.numero_telefono,
            idSelectDniNie: fetched.idSelectDniNie ?? prev.idSelectDniNie,
            dniNie: fetched.dniNie ?? prev.dniNie,
            calle: (fetched.calle as string) ?? prev.calle,
            locality: fetched.locality ?? prev.locality,
            province: fetched.province ?? prev.province,
            postalCode: fetched.postalCode ?? prev.postalCode,
            nombre_empresa: fetched.nombre_empresa ?? prev.nombre_empresa,
            cif: fetched.cif ?? prev.cif,
            // NOTA: no ponemos usuario_id aquí porque formDataPaso1 no lo define en su tipo
          }));
        }
      } catch (err: any) {
        setError(err.message);
      }
    };

    fetchSuscriptor();
  }, [userName, currentStep]);

  const fetchSuscripcion = async () => {
    if (!formData1.usuario_id) return alert("Usuario no identificado");

    try {
      const res = await safeFetch(`/api/nuevaSuscripcion`, {
        method: "POST",
        body: JSON.stringify({
          usuario_id: formData1.usuario_id,
          username: userName,
          ...formData1,
          selectedProfesiones: formData2.selectedProfession,
          paymentOption: formData2.paymentOption,
          paymentAmount,
        }),
      });

      const data = await res.json();
      if (!data.idToken) throw new Error(data.message || "No se recibió token");

      // 👉 Guardar token recién generado
      localStorage.setItem("token", data.idToken);
      window.dispatchEvent(new Event("tokenChanged"));

      // 👉 Guardar factura si existe
      if (facturaRef.current) {
        const base64Factura = await facturaRef.current.generarPdfComoBase64();
        if (base64Factura) sessionStorage.setItem("pdfFacturaBase64", base64Factura);
      }

      setCurrentStep(3);
      alert("Suscripción enviada con éxito");

    } catch (err: any) {
      console.error(err);
      setError(err.message || "Error desconocido");
    }
  };


  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateStep()) if (currentStep < 3) setCurrentStep(prev => prev + 1);
  };

  if (error) return <div>Error: {error}</div>;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 overflow-x-hidden">
      <div className="w-full max-w-6xl">
        <StepProgressBar totalSteps={4} currentStep={currentStep} />
        <div className="mt-6 rounded-2xl shadow-lg shadow-zinc-600 border border-black border-b-2 border-r-8 bg-white px-6 py-8">
          <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
            {currentStep === 1 && (

              <FormularioPaso1
                formData1={formDataPaso1}
                setFormData1={setFormDataPaso1}
                isEmpresa={isEmpresa}
                handleToggleEmpresa={() => setIsEmpresa(!isEmpresa)}
                errors={errors}
                setErrors={setErrors}
                userName={userName}
                roles={roles}
                allowEditDni={unlockDni}
              />
            )}

            {currentStep === 2 && (
              <FormularioPaso2
                selectedProfesiones={formData2.selectedProfession}
                handleProfesionSelect={(selected) =>
                  setFormData2(prev => ({ ...prev, selectedProfession: selected }))
                }
                paymentOption={formData2.paymentOption}
                onPaymentChange={(option, amount) => {
                  setFormData2(prev => ({ ...prev, payment: amount, paymentOption: option }));
                  setPaymentOption(option);
                  setPaymentAmount(amount);
                }}
                errors={errors}
                setErrors={setErrors}
              />
            )}

            {currentStep === 3 && (
              <FormularioPaso3
                selectedProfesiones={formData2.selectedProfession}
                paymentOption={paymentOption}
                paymentAmount={paymentAmount}

                onTotalPagarChange={setTotalPagar}

                formData1={formDataPaso1}
                setFormData1={setFormDataPaso1}
                setFormData2={setFormData2}
                isEmpresa={isEmpresa}
                handleProfesionSelect={(selected) =>
                  setFormData2(prev => ({ ...prev, selectedProfession: selected }))
                }
                setPaymentOption={setPaymentOption}
                onPaymentChange={(option: PaymentOption, amount: number) => {
                  setFormData2(prev => ({ ...prev, payment: amount, paymentOption: option }));
                  setPaymentAmount(amount);
                  setPaymentOption(option);
                }}
                setPaymentAmount={setPaymentAmount}
                onGuardarDatosUsuario={fetchSuscripcion}
                onPagoFinalizado={fetchSuscripcion}
                isFirmaAccepted={isFirmaAccepted}
                modoRenovar={false}
                errors={errors}
              />
            )}


            {currentStep < 3 && (
              <div className="flex mx-auto justify-center h-4 items-center w-3/4 p-4">
                <ButtonSiguiente onClick={handleNext} >
                  {currentStep === 3 ? "Enviar" : "Siguiente"}
                </ButtonSiguiente>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default Subs;
